import { useId } from 'react';
import { formatMoneyShort } from '../lib/format.js';

export interface Point { date: string; value: number }

/* ------------------------------------------------------------------ paths */

function smoothPath(points: { x: number; y: number }[]): string {
  if (points.length === 0) return '';
  if (points.length === 1) return `M ${points[0]!.x} ${points[0]!.y}`;
  let d = `M ${points[0]!.x} ${points[0]!.y}`;
  for (let i = 1; i < points.length; i++) {
    const previous = points[i - 1]!;
    const current = points[i]!;
    const midX = (previous.x + current.x) / 2;
    d += ` C ${midX} ${previous.y}, ${midX} ${current.y}, ${current.x} ${current.y}`;
  }
  return d;
}

function scale(points: Point[], width: number, height: number, padTop: number, padBottom: number) {
  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || Math.abs(max) || 1;
  const usable = height - padTop - padBottom;
  return points.map((p, i) => ({
    x: points.length === 1 ? width / 2 : (i / (points.length - 1)) * width,
    y: padTop + usable - ((p.value - min) / span) * usable,
  }));
}

/* -------------------------------------------------------------- area chart */

interface AreaChartProps {
  points: Point[];
  height?: number;
  color?: string;
  /** Gridlines + axis labels, as on the hero card. */
  axes?: boolean;
  /** Hebrew month name for the x-axis labels. */
  monthLabel?: string;
  ariaLabel: string;
}

export function AreaChart({
  points, height = 96, color = 'var(--positive)', axes = true, monthLabel, ariaLabel,
}: AreaChartProps) {
  const id = useId().replace(/:/g, '');
  const width = 360;
  const padTop = 8;
  const padBottom = axes ? 18 : 6;
  // Room for the value labels down the left edge, as in the design.
  const padLeft = axes ? 40 : 0;
  const padRight = axes ? 6 : 0;
  const plotWidth = width - padLeft - padRight;

  if (points.length === 0) {
    return <div style={{ height }} role="img" aria-label={ariaLabel} />;
  }

  const coords = scale(points, plotWidth, height, padTop, padBottom).map((p) => ({ ...p, x: p.x + padLeft }));
  const line = smoothPath(coords);
  const area = `${line} L ${padLeft + plotWidth} ${height - padBottom} L ${padLeft} ${height - padBottom} Z`;
  const last = coords.at(-1)!;

  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const gridValues = axes ? [max, min + (max - min) / 2, min] : [];

  const dayLabel = (iso: string) => `${Number(iso.slice(8, 10))}${monthLabel ? ` ב${monthLabel}` : ''}`;
  const middle = points[Math.floor((points.length - 1) / 2)]!;
  const xLabels = axes
    ? [
        { at: 0, text: dayLabel(points[0]!.date), anchor: 'start' as const },
        ...(points.length > 4 ? [{ at: 0.5, text: dayLabel(middle.date), anchor: 'middle' as const }] : []),
        { at: 1, text: dayLabel(points.at(-1)!.date), anchor: 'end' as const },
      ]
    : [];

  return (
    <svg
      className="chart"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={ariaLabel}
      // Anchoring is computed left-to-right; the Hebrew inside each label still
      // renders right-to-left, so labels stay inside the plot on both directions.
      style={{ width: '100%', height: 'auto', direction: 'ltr' }}
    >
      <defs>
        <linearGradient id={`fill-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.34" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>

      {gridValues.map((value, i) => {
        const y = padTop + ((height - padTop - padBottom) / (gridValues.length - 1)) * i;
        return (
          <g key={i}>
            <line
              x1={padLeft} x2={width - padRight} y1={y} y2={y}
              stroke="var(--hero-grid)" strokeWidth="1" strokeDasharray="4 5"
            />
            <text x={padLeft - 6} y={y + 3} textAnchor="end" fontSize="9" fill="var(--hero-text-2)">
              {formatMoneyShort(value)}
            </text>
          </g>
        );
      })}

      <path d={area} fill={`url(#fill-${id})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last.x} cy={last.y} r="4.5" fill={color} stroke="#fff" strokeWidth="2" />

      {xLabels.map((label) => (
        <text
          key={label.at}
          x={padLeft + label.at * plotWidth}
          y={height - 4}
          textAnchor={label.anchor}
          fontSize="9"
          fill="var(--hero-text-2)"
        >
          {label.text}
        </text>
      ))}
    </svg>
  );
}

/* --------------------------------------------------------------- sparkline */

export function Sparkline({ points, color, height = 34 }: { points: Point[]; color: string; height?: number }) {
  const id = useId().replace(/:/g, '');
  if (points.length < 2) return <div style={{ height }} aria-hidden="true" />;
  const width = 120;
  const coords = scale(points, width, height, 3, 3);
  const line = smoothPath(coords);
  const area = `${line} L ${width} ${height} L 0 ${height} Z`;

  return (
    <svg className="chart" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ height }} aria-hidden="true">
      <defs>
        <linearGradient id={`spark-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#spark-${id})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/* ------------------------------------------------------------------ donut */

export interface DonutSlice { id: string; value: number; color: string; label: string }

export function Donut({
  slices, size = 150, thickness = 26, centerTop, centerBottom,
}: { slices: DonutSlice[]; size?: number; thickness?: number; centerTop: string; centerBottom: string }) {
  const total = slices.reduce((a, s) => a + s.value, 0);
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div style={{ position: 'relative', width: size, height: size, flex: 'none' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`${centerBottom}: ${centerTop}`}>
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          {total === 0 && (
            <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="var(--surface-sunken)" strokeWidth={thickness} />
          )}
          {slices.map((slice) => {
            const fraction = total === 0 ? 0 : slice.value / total;
            const dash = fraction * circumference;
            const element = (
              <circle
                key={slice.id}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={slice.color}
                strokeWidth={thickness}
                strokeDasharray={`${Math.max(0, dash - 2)} ${circumference}`}
                strokeDashoffset={-offset}
                strokeLinecap="butt"
              />
            );
            offset += dash;
            return element;
          })}
        </g>
      </svg>
      <div
        className="donut-center"
        style={{ position: 'absolute', inset: 0, display: 'grid', placeContent: 'center' }}
      >
        <strong className="money">{centerTop}</strong>
        <span>{centerBottom}</span>
      </div>
    </div>
  );
}
